import { useState } from 'react'
import { Bar1v1 } from '../components/Bar100'
import { Bar1v14Bars } from '../components/Bar100-4Bars'
import { useSocketEvent, useSocketState } from '../helpers/useSocket'
import { LiveGameAgeIndicator } from '../components/LiveGame/LiveGameAgeIndicator'
import { LiveGameNamePlate } from '../components/LiveGame/LiveGameNamePlate'
import { LiveGamePopulation } from '../components/LiveGame/LiveGamePopulation'
import { LiveGameRelics } from '../components/LiveGame/LiveGameRelics'
import { LiveGameResource } from '../components/LiveGame/LiveGameResource'
import { LiveGameSeperator } from '../components/LiveGame/LiveGameSeperator'
import { LiveGameIdleVillagers } from '../components/LiveGame/LiveGameIdleVillagers'
import { LiveGameAgeUpProgress } from '../components/LiveGame/LiveGameAgeUpProgress'

import colors from '../config/colors.json'
import { LiveGameExtraEco } from '../components/LiveGame/LiveGameExtraEco'
import { LiveGameSiege } from '../components/LiveGame/LiveGameSiege'
import { LiveGameWarships } from '../components/LiveGame/LiveGameWarships'
import { LiveGameTime } from '../components/LiveGame/LiveGameTime'
import { canAgeUp } from '../helpers/canAgeUp'
import { LiveGameMilitary } from '../components/LiveGame/LiveGameMilitary'
import { filterOutEconomy } from '../helpers/filterSquads'
import { LiveGameProductionIndicator } from '../components/LiveGame/LiveGameProductionIndicator'
import { LiveGameKeyTech } from '../components/LiveGame/LiveGameKeyTech'

import { LiveGameWinCondition } from '../components/LiveGame/LiveGameWinCondition'
import { LiveGameCivUnique } from '../components/LiveGame/LiveGameCivUnique'
import { LiveGameAgeUpAsUpgrade } from '../components/LiveGame/LiveGameAgeUpAsUpgrade'
import { LiveGameOverviewer } from '../components/LiveGame/LiveGameOverviewer'
import classNames from 'classnames'

import { useQuery } from 'react-query'
import { LiveGameMinimap } from '../components/LiveGame/LiveGameMinimap'
import { LiveGameGraph } from '../components/LiveGame/LiveGameGraph'
import LiveGameStatistics from '../components/LiveGame/LiveGameStatistics'
import { LiveGameGraphComparison } from '../components/LiveGame/LiveGameGraphComparison'

import { LiveGameSheepCounter } from '../components/LiveGame/LiveGameSheepCounter'
import { getBackendUrl } from '../helpers/url'

export default function LiveGameOverlay() {
	const [config, setConfig] = useState({})
	const [match] = useSocketState('Match')
	const [tournament] = useSocketState('Tournament')
	const [overviewState, setOverviewState] = useState(false)

	const fetchCivs = async () => {
		const res = await fetch(`${getBackendUrl()}/api/config/civs`)
		return res.json()
	}

	const { data: civs } = useQuery('civs', fetchCivs)

	useSocketEvent('updateConfig', (newdata) => {
		console.log(`Received updateConfig`, newdata)
		setConfig(newdata)
	})

	useSocketEvent('updateOverview', (newData) => {
		console.log(newData)
		setOverviewState(newData)
	})

	const [gameState] = useSocketState('LiveGameOverlay')

	if (!gameState) return <div />

	let leftColor = match.flipPlayersIngame
		? match.rightPlayer.color
		: match.leftPlayer.color
	let rightColor = match.flipPlayersIngame
		? match.leftPlayer.color
		: match.rightPlayer.color

	let leftCountry = match.flipPlayersIngame
		? match.rightPlayer.country
		: match.leftPlayer.country
	let rightCountry = match.flipPlayersIngame
		? match.leftPlayer.country
		: match.rightPlayer.country

	let leftCiv = civs?.find(
		(value) =>
			value.aoe2cm === gameState?.leftPlayer?.civilization ||
			value.label === gameState?.leftPlayer?.civilization,
	)
	let rightCiv = civs?.find(
		(value) =>
			value.aoe2cm === gameState?.rightPlayer?.civilization ||
			value.label === gameState?.rightPlayer?.civilization,
	)

	let leftPlayerInfo = tournament?.config?.players?.find((p) =>
		gameState?.leftPlayer?.name
			?.toLowerCase()
			.includes(p.name.toLowerCase()),
	)
	let rightPlayerInfo = tournament?.config?.players?.find((p) =>
		gameState?.rightPlayer?.name
			?.toLowerCase()
			.includes(p.name.toLowerCase()),
	)

	return (
		<div
			className="liveGameOverlay h-[100vh] "
			style={{
				'--left-player-color': colors[leftColor]?.mainColor,
				'--left-player-color-alpha': colors[leftColor]?.mainColorAlpha,
				'--left-player-color-alpha-7':
					colors[leftColor]?.mainColorAlpha7,
				'--left-player-color-secondary':
					colors[leftColor]?.secondaryColor,
				'--right-player-color': colors[rightColor]?.mainColor,
				'--right-player-color-alpha':
					colors[rightColor]?.mainColorAlpha,
				'--right-player-color-alpha-7':
					colors[rightColor]?.mainColorAlpha7,
				'--right-player-color-secondary':
					colors[rightColor]?.secondaryColor,
			}}
		>
			<link
				rel="stylesheet"
				type="text/css"
				href={`/themes/${config.cssTheme}`}
			/>
			<img
				className="liveGameOverlay__outline absolute -z-20"
				src="/liveGameIcons/TopbarOutline.svg"
				alt=""
				style={{
					backgroundImage: 'url(/liveGameIcons/BACKGROUND.png)',
					WebkitMaskImage: 'url(/liveGameIcons/Topbar.svg)',
					maskImage: 'url(/liveGameIcons/Topbar.svg)',
					maskRepeat: 'no-repeat',
				}}
			/>
			<div className="liveGameOverlay__topBar">
				<div className="liveGameOverlay__topBar-leftPlayer">
					<div className="liveGameOverlay__topBar-leftPlayer-info flex flex-row absolute">
						<LiveGameResource
							leftSide={true}
							resourceCount={
								gameState?.leftPlayer?.resources?.foodCurrent
							}
							resourcePerMin={
								gameState?.leftPlayer?.resources?.foodPerMin
							}
							resourceVills={
								gameState?.leftPlayer?.resources?.foodWorker
							}
							resourceType={'food'}
						/>
						<LiveGameSeperator />
						<LiveGameResource
							leftSide={true}
							resourceCount={
								gameState?.leftPlayer?.resources?.woodCurrent
							}
							resourcePerMin={
								gameState?.leftPlayer?.resources?.woodPerMin
							}
							resourceVills={
								gameState?.leftPlayer?.resources?.woodWorker
							}
							resourceType={'wood'}
						/>
						<LiveGameSeperator />
						<LiveGameResource
							leftSide={true}
							resourceCount={
								gameState?.leftPlayer?.resources?.goldCurrent
							}
							resourcePerMin={
								gameState?.leftPlayer?.resources?.goldPerMin
							}
							resourceVills={
								gameState?.leftPlayer?.resources?.goldWorker
							}
							resourceType={'gold'}
						/>
						<LiveGameSeperator />
						<LiveGameResource
							leftSide={true}
							resourceCount={
								gameState?.leftPlayer?.resources?.stoneCurrent
							}
							resourcePerMin={
								gameState?.leftPlayer?.resources?.stonePerMin
							}
							resourceVills={
								gameState?.leftPlayer?.resources?.stoneWorker
							}
							resourceType={'stone'}
						/>
						<LiveGameSeperator />
						<div className="populationRelic flex flex-col justify-center pl-4 w-[118px]">
							<LiveGamePopulation
								currentPopulation={
									gameState?.leftPlayer?.currentPopulation
								}
								maxPopulation={
									gameState?.leftPlayer?.maxPopulation
								}
								leftSide={true}
							/>
							<LiveGameRelics
								currentRelics={gameState?.leftPlayer?.relics}
								carriedRelics={
									gameState?.leftPlayer?.relicsCarried
								}
								leftSide={true}
								display={gameState?.leftPlayer?.age > 2}
								upgrades={
									gameState?.leftPlayer
										?.technologies_researched
								}
							/>
						</div>
						<div className="ml-14 w-[162px]">
							<LiveGameNamePlate
								name={
									leftPlayerInfo?.name ||
									gameState?.leftPlayer?.name
								}
								civ={gameState?.leftPlayer?.civilization}
								country={leftPlayerInfo?.country || leftCountry}
								color={leftColor}
								leftSide={true}
								teamIcon={
									leftPlayerInfo?.teamPictureUrl || null
								}
							/>
						</div>
						<div className="absolute left-[810px] w-24">
							<LiveGameAgeIndicator
								currentAge={gameState?.leftPlayer?.age}
								canAgeUp={canAgeUp({
									age: gameState?.leftPlayer?.age,
									food: gameState?.leftPlayer?.resources
										?.foodCurrent,
									gold: gameState?.leftPlayer?.resources
										?.goldCurrent,
								})}
							/>
						</div>
					</div>
					<div
						className={classNames(
							gameState?.leftPlayer?.civilization ===
								'Macedonian Dynasty'
								? 'w-[220px]'
								: 'w-[176px]',
							'liveGameOverlay__topBar-leftPlayer-keyTechs flex flex-row absolute top-[96px] left-[0px] h-[106px] max-w-[440px] flex-wrap ml-2 mt-2 justify-start',
						)}
					>
						{leftCiv?.keyTechs.map((tech, index) => (
							<LiveGameKeyTech
								key={index}
								color={leftColor}
								tech={tech}
								currentUpgrades={
									gameState?.leftPlayer
										?.technologies_researched
								}
							/>
						))}
					</div>
					<div className="liveGameOverlay__topBar-leftPlayer-VictoryAgeUp flex flex-col absolute top-[220px] left-[0px] gap-2 items-start">
						{/* <ChinaDynasty allUpgrades={gameState?.leftPlayer?.technologies_researched} leftSide={true} civilzation={gameState?.leftPlayer?.civilization} /> */}
						{gameState?.leftPlayer?.ageUpProgress && (
							// Temp fix until we get proper data from the server, absolutely terrible code xd
							<LiveGameAgeUpProgress
								pgbid={
									gameState?.leftPlayer?.ageUpProgress['2']
								}
								progress={
									gameState?.leftPlayer?.ageUpProgress['5']
								}
								workers={
									gameState?.leftPlayer?.ageUpProgress['4']
								}
								estimatedTimeLeft={
									gameState?.leftPlayer?.ageUpProgress['3']
								}
								leftSide={true}
								color={leftColor}
							/>
						)}
						{gameState?.leftPlayer?.ageUpAsUpgrade && (
							<LiveGameAgeUpAsUpgrade
								//yea,, i know
								pgbid={
									gameState?.leftPlayer?.ageUpAsUpgrade['3']
								}
								remainingTime={
									gameState?.leftPlayer?.ageUpAsUpgrade['4']
								}
								leftSide={true}
								color={leftColor}
							/>
						)}
						<LiveGameWinCondition
							leftSide={true}
							data={gameState?.leftPlayer?.winCondition}
						/>
					</div>
					<div
						className={classNames(
							gameState?.leftPlayer?.civilization ===
								'Macedonian Dynasty'
								? 'left-[436px] w-[370px]'
								: 'left-[366px] w-[440px]',
							'liveGameOverlay__topBar-leftPlayer-military flex flex-row-reverse absolute top-[96px] h-[120px] max-w-[440px] flex-wrap',
						)}
					>
						<LiveGameProductionIndicator />
						<div className="absolute flex flex-row-reverse flex-wrap mr-5">
							{
								//Pass in the all the squads, filter out the economy ones and map the rest to LiveGameMilitary
								filterOutEconomy([
									...new Set([
										...(
											gameState?.leftPlayer?.squads ?? []
										).map((s) => Number(s.pbg_id)),
										...Object.values(
											gameState?.leftPlayer
												?.production_queue ?? {},
										).map(Number),
									]),
								]).map((id, index) => (
									<LiveGameMilitary
										key={id}
										pbgid={id}
										count={
											(
												gameState?.leftPlayer?.squads ??
												[]
											).find(
												(s) => Number(s.pbg_id) === id,
											)?.count ?? 0
										}
										inQueue={Object.values(
											gameState?.leftPlayer
												?.production_queue ?? {},
										).reduce(
											(n, v) =>
												n + (Number(v) === id ? 1 : 0),
											0,
										)}
										leftSide={true}
									/>
								))
							}
						</div>
					</div>
					<div
						className={classNames(
							gameState?.leftPlayer?.civilization ===
								'Macedonian Dynasty'
								? 'left-[230px]'
								: 'left-[186px]',
							'liveGameOverlay__topBar-leftPlayer-uniqueCiv absolute mt-2 left-[186px] top-[96px] w-[184px] h-[88px] flex flex-row flex-wrap gap-2 content-start',
						)}
					>
						<LiveGameCivUnique
							data={gameState?.leftPlayer}
							color={leftColor}
						/>
					</div>
					<div
						className={classNames(
							'liveGameOverlay__topBar-leftPlayer-overview absolute left-0 top-[390px] overview overviewLeft',
							overviewState && 'overviewLeftShow',
						)}
					>
						<LiveGameOverviewer
							playerData={gameState?.leftPlayer}
							color={leftColor}
							leftSide={true}
						/>
					</div>
				</div>

				<div className="liveGameOverlay__center flex flex-col items-center absolute left-1/2 -translate-x-1/2 w-[304px] h-[132px]">
					<div className="liveGameOverlay__center__firstrow flex flex-row text-white text-2xl w-9/12 justify-center items-center -mb-2">
						<div className="flex-1 flex justify-start">
							{gameState?.leftPlayer?.idleEconomy === 0 &&
							gameState?.leftPlayer?.fishingShips === 0 &&
							gameState?.leftPlayer?.traders ===
								0 ? null : gameState?.leftPlayer?.idleEconomy >
							  0 ? (
								<LiveGameIdleVillagers
									idleVillagers={
										gameState?.leftPlayer?.idleEconomy
									}
									leftSide={true}
								/>
							) : gameState?.leftPlayer?.fishingShips > 0 ||
							  gameState?.leftPlayer?.traders > 0 ? (
								<LiveGameExtraEco
									fishingShips={
										gameState?.leftPlayer?.fishingShips
									}
									traders={gameState?.leftPlayer?.traders}
									leftSide={true}
								/>
							) : null}
						</div>
						<div className="flex-1 flex justify-end">
							{gameState?.leftPlayer?.totalEconomy}
						</div>
						<img
							className="h-[50px] mx-2"
							src="/liveGameIcons/resource_villager.png"
							alt=""
						/>
						<div className="flex-1 flex justify-start">
							{gameState?.rightPlayer?.totalEconomy}
						</div>
						<div className="flex-1 flex justify-end">
							{gameState?.rightPlayer?.idleVillagers === 0 &&
							gameState?.rightPlayer?.fishingShips === 0 &&
							gameState?.rightPlayer?.traders ===
								0 ? null : gameState?.rightPlayer?.idleEconomy >
							  0 ? (
								<LiveGameIdleVillagers
									idleVillagers={
										gameState?.rightPlayer?.idleEconomy
									}
									leftSide={false}
								/>
							) : gameState?.rightPlayer?.fishingShips > 0 ||
							  gameState?.rightPlayer?.traders > 0 ? (
								<LiveGameExtraEco
									fishingShips={
										gameState?.rightPlayer?.fishingShips
									}
									traders={gameState?.rightPlayer?.traders}
									leftSide={false}
								/>
							) : null}
						</div>
					</div>
					<div className="liveGameOverlay__bar w-9/12 ">
						<Bar1v14Bars
							name="vills"
							player1={gameState?.leftPlayer?.totalEconomy}
							player1alt={gameState?.leftPlayer?.idleEconomy}
							player2={gameState?.rightPlayer?.totalEconomy}
							player2alt={gameState?.rightPlayer?.idleEconomy}
							leftColor={colors[leftColor]?.mainColor}
							rightColor={colors[rightColor]?.mainColor}
						/>
						<Bar1v1
							name="military"
							player1={gameState?.leftPlayer?.militaryCount}
							player2={gameState?.rightPlayer?.militaryCount}
							leftColor={colors[leftColor]?.mainColor}
							rightColor={colors[rightColor]?.mainColor}
						/>
					</div>
					<div className="liveGameOverlay__center__secondrow flex flex-row text-white text-2xl w-9/12 justify-center items-start -mb-2">
						<div className="flex-1 flex justify-start">
							<LiveGameSiege
								siege={gameState?.leftPlayer?.siege}
								leftSide={true}
							/>
						</div>
						<div className="flex-1 flex justify-end">
							{gameState?.leftPlayer?.militaryCount}
						</div>
						<img
							className="h-[40px] mx-2"
							src="/liveGameIcons/resource_military.png"
							alt=""
						/>
						<div className="flex-1 flex justify-start">
							{gameState?.rightPlayer?.militaryCount}
						</div>
						<div className="flex-1 flex justify-end">
							<LiveGameSiege
								siege={gameState?.rightPlayer?.siege}
								leftSide={false}
							/>
						</div>
					</div>
					<div className="liveGameOverlay__center__ingameTimer text-xl text-white -mt-1 flex flex-row w-9/12 justify-center items-center">
						<div className="flex-1 flex justify-start">
							<LiveGameWarships
								warships={gameState?.leftPlayer?.warships}
								leftSide={true}
							/>
						</div>
						<LiveGameTime time={gameState.gameTime} />
						<div className="flex-1 flex justify-end">
							<LiveGameWarships
								warships={gameState?.rightPlayer?.warships}
								leftSide={false}
							/>
						</div>
					</div>
				</div>
				<div className="liveGameOverlay__topBar-rightPlayer">
					<div className="liveGameOverlay__topBar-rightPlayer-info flex flex-row absolute right-0">
						<div className="absolute right-[710px] w-24">
							<LiveGameAgeIndicator
								currentAge={gameState?.rightPlayer?.age}
								canAgeUp={canAgeUp({
									age: gameState?.rightPlayer?.age,
									food: gameState?.rightPlayer?.resources
										?.foodCurrent,
									gold: gameState?.rightPlayer?.resources
										?.goldCurrent,
								})}
							/>
						</div>
						<div className="mr-14 w-[162px]">
							<LiveGameNamePlate
								name={
									rightPlayerInfo?.name ||
									gameState?.rightPlayer?.name
								}
								civ={gameState?.rightPlayer?.civilization}
								country={
									rightPlayerInfo?.country || rightCountry
								}
								color={rightColor}
								leftSide={false}
								teamIcon={
									rightPlayerInfo?.teamPictureUrl || null
								}
							/>
						</div>
						<div className="populationRelic flex flex-col justify-center pr-4 w-[120px]">
							<LiveGamePopulation
								currentPopulation={
									gameState?.rightPlayer?.currentPopulation
								}
								maxPopulation={
									gameState?.rightPlayer?.maxPopulation
								}
								leftSide={false}
							/>
							<LiveGameRelics
								currentRelics={gameState?.rightPlayer?.relics}
								carriedRelics={
									gameState?.rightPlayer?.relicsCarried
								}
								leftSide={false}
								display={gameState?.rightPlayer?.age > 2}
								upgrades={
									gameState?.rightPlayer
										?.technologies_researched
								}
							/>
						</div>
						<LiveGameSeperator />
						<LiveGameResource
							leftSide={false}
							resourceCount={
								gameState?.rightPlayer?.resources?.foodCurrent
							}
							resourcePerMin={
								gameState?.rightPlayer?.resources?.foodPerMin
							}
							resourceVills={
								gameState?.rightPlayer?.resources?.foodWorker
							}
							resourceType={'food'}
						/>
						<LiveGameSeperator />
						<LiveGameResource
							leftSide={false}
							resourceCount={
								gameState?.rightPlayer?.resources?.woodCurrent
							}
							resourcePerMin={
								gameState?.rightPlayer?.resources?.woodPerMin
							}
							resourceVills={
								gameState?.rightPlayer?.resources?.woodWorker
							}
							resourceType={'wood'}
						/>
						<LiveGameSeperator />
						<LiveGameResource
							leftSide={false}
							resourceCount={
								gameState?.rightPlayer?.resources?.goldCurrent
							}
							resourcePerMin={
								gameState?.rightPlayer?.resources?.goldPerMin
							}
							resourceVills={
								gameState?.rightPlayer?.resources?.goldWorker
							}
							resourceType={'gold'}
						/>
						<LiveGameSeperator />
						<LiveGameResource
							leftSide={false}
							resourceCount={
								gameState?.rightPlayer?.resources?.stoneCurrent
							}
							resourcePerMin={
								gameState?.rightPlayer?.resources?.stonePerMin
							}
							resourceVills={
								gameState?.rightPlayer?.resources?.stoneWorker
							}
							resourceType={'stone'}
						/>
					</div>
					<div className="liveGameOverlay__topBar-rightPlayer-VictoryAgeUp flex flex-col absolute top-[220px] right-[0px] gap-2 items-end">
						{/* <ChinaDynasty allUpgrades={gameState?.rightPlayer?.technologies_researched} leftSide={true} civilzation={gameState?.rightPlayer?.civilization} /> */}
						{gameState?.rightPlayer?.ageUpProgress && (
							// Temp fix until we get proper data from the server, absolutely terrible code xd
							<LiveGameAgeUpProgress
								pgbid={
									gameState?.rightPlayer?.ageUpProgress['2']
								}
								progress={
									gameState?.rightPlayer?.ageUpProgress['5']
								}
								workers={
									gameState?.rightPlayer?.ageUpProgress['4']
								}
								estimatedTimeLeft={
									gameState?.rightPlayer?.ageUpProgress['3']
								}
								leftSide={false}
								color={rightColor}
							/>
						)}
						{gameState?.rightPlayer?.ageUpAsUpgrade && (
							<LiveGameAgeUpAsUpgrade
								//yea,, i know
								pgbid={
									gameState?.rightPlayer?.ageUpAsUpgrade['3']
								}
								remainingTime={
									gameState?.rightPlayer?.ageUpAsUpgrade['4']
								}
								leftSide={false}
								color={rightColor}
							/>
						)}
						<LiveGameWinCondition
							leftSide={false}
							data={gameState?.rightPlayer?.winCondition}
						/>
					</div>
					<div
						className={classNames(
							gameState?.rightPlayer?.civilization ===
								'Macedonian Dynasty'
								? 'right-[436px] w-[370px]'
								: 'right-[366px] w-[440px]',
							'liveGameOverlay__topBar-rightPlayer-military flex flex-row absolute top-[96px] h-[120px]',
						)}
					>
						<LiveGameProductionIndicator />
						<div className="absolute flex flex-row flex-wrap ml-5">
							{
								//Pass in the all the squads, filter out the economy ones and map the rest to LiveGameMilitary
								filterOutEconomy([
									...new Set([
										...(
											gameState?.rightPlayer?.squads ?? []
										).map((s) => Number(s.pbg_id)),
										...Object.values(
											gameState?.rightPlayer
												?.production_queue ?? {},
										).map(Number),
									]),
								]).map((id, index) => (
									<LiveGameMilitary
										key={id}
										pbgid={id}
										count={
											(
												gameState?.rightPlayer
													?.squads ?? []
											).find(
												(s) => Number(s.pbg_id) === id,
											)?.count ?? 0
										}
										inQueue={Object.values(
											gameState?.rightPlayer
												?.production_queue ?? {},
										).reduce(
											(n, v) =>
												n + (Number(v) === id ? 1 : 0),
											0,
										)}
										leftSide={false}
									/>
								))
							}
						</div>
					</div>
					<div
						className={classNames(
							gameState?.rightPlayer?.civilization ===
								'Macedonian Dynasty'
								? 'w-[220px]'
								: 'w-[176px]',
							'liveGameOverlay__topBar-rightPlayer-keyTechs flex flex-row absolute top-[96px] right-[0px] h-[106px] flex-wrap mr-2 mt-2 justify-end',
						)}
					>
						{rightCiv?.keyTechs.map((tech, index) => (
							<LiveGameKeyTech
								key={index}
								color={rightColor}
								tech={tech}
								currentUpgrades={
									gameState?.rightPlayer
										?.technologies_researched
								}
							/>
						))}
					</div>
					<div
						className={classNames(
							gameState?.rightPlayer?.civilization ===
								'Macedonian Dynasty'
								? 'right-[230px] '
								: 'right-[186px]',
							'liveGameOverlay__topBar-rightPlayer-uniqueCiv absolute mt-2 top-[96px] max-w-[184px] flex flex-row-reverse flex-wrap gap-2 items-end content-end ',
						)}
					>
						<LiveGameCivUnique
							data={gameState?.rightPlayer}
							color={rightColor}
						/>
					</div>
					<div
						className={classNames(
							'liveGameOverlay__topBar-rightPlayer-overview absolute right-0 top-[390px] overview overviewRight',
							overviewState && 'overviewRightShow',
						)}
					>
						<LiveGameOverviewer
							playerData={gameState?.rightPlayer}
							color={rightColor}
							leftSide={false}
						/>
					</div>
				</div>
			</div>
			<div>
				<LiveGameGraph
					leftColor={leftColor}
					rightColor={rightColor}
					height="280px"
					width="530px"
					useAnnotations
					id={'Economy'}
					name="Economy Count"
				/>
				<LiveGameGraph
					leftColor={leftColor}
					rightColor={rightColor}
					height="280px"
					width="530px"
					useAnnotations
					id={'Relics'}
					name="Relics / Carried"
				/>
				<LiveGameGraph
					leftColor={leftColor}
					rightColor={rightColor}
					height="280px"
					width="530px"
					useAnnotations
					id={'Military'}
					name="Military Count"
				/>
				<LiveGameGraph
					leftColor={leftColor}
					rightColor={rightColor}
					height="280px"
					width="530px"
					id={'Score'}
					name="Score"
				/>
				<LiveGameGraph
					leftColor={leftColor}
					rightColor={rightColor}
					height="280px"
					width="530px"
					useAnnotations
					id={'TotalResPerMin'}
					name="Total Resources /Min"
				/>
				<LiveGameGraph
					leftColor={leftColor}
					rightColor={rightColor}
					height="280px"
					width="530px"
					useAnnotations
					id={'TotalRes'}
					name="Total Resources in Bank"
					granularity={20}
				/>
				<LiveGameGraph
					leftColor={leftColor}
					rightColor={rightColor}
					height="280px"
					width="530px"
					useAnnotations
					id={'TotalGathered'}
					name="Total Resources Collected"
					granularity={10}
				/>
				<LiveGameGraph
					leftColor={leftColor}
					rightColor={rightColor}
					height="280px"
					width="530px"
					useAnnotations
					id={'TotalTCIdleTime'}
					name="Total Towncenter Idle"
					granularity={10}
				/>
				<LiveGameGraph
					leftColor={leftColor}
					rightColor={rightColor}
					height="280px"
					width="530px"
					useAnnotations
					id={'TotalVillagersIdleTime'}
					name="Total Villagers Idle"
					granularity={10}
				/>
				<LiveGameGraphComparison
					leftColor={leftColor}
					rightColor={rightColor}
					height="280px"
					width="530px"
					useAnnotations
					id={'ArmyValue'}
					name="Army Value Lead"
					granularity={40}
				/>
			</div>
			<div>
				<LiveGameMinimap
					width={242}
					height={242}
					minimapData={gameState.minimapData}
					leftColor={leftColor}
					rightColor={rightColor}
				/>
			</div>
			<LiveGameStatistics
				leftPlayer={gameState?.leftPlayer}
				rightPlayer={gameState?.rightPlayer}
				leftColor={leftColor}
				rightColor={rightColor}
			/>

			<LiveGameSheepCounter
				leftColor={leftColor}
				rightColor={rightColor}
			/>
		</div>
	)
}
